import { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { Database, Play, Loader2, CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

interface ScrapeJob {
  id: string;
  marketplace: string;
  status: string;
  domains_found: number;
  domains_created: number;
  domains_updated: number;
  listings_created: number;
  listings_updated: number;
  errors: number;
  error_log: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

interface ScrapeOverview {
  available_scrapers: string[];
  total_jobs: number;
  running_jobs: number;
  last_completed: ScrapeJob | null;
  recent_jobs: ScrapeJob[];
}

export default function AdminScraping() {
  const [overview, setOverview] = useState<ScrapeOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fetchOverview = async () => {
    try {
      const res = await api.get('/api/scraping/overview');
      setOverview(res.data);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  const triggerScrape = async (marketplace?: string) => {
    setTriggering(marketplace || 'all');
    setError('');
    try {
      const res = await api.post('/api/scraping/trigger', {
        marketplace: marketplace || null,
        max_pages: 5,
      });
      alert(res.data.message);
      setTimeout(fetchOverview, 5000);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setTriggering(null);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running': return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      running: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      pending: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] || styles.pending}`}>
        {statusIcon(status)} {status}
      </span>
    );
  };

  if (loading) return <DashboardLayout><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Scraping Pipeline</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Automated price collection from guest post marketplaces</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchOverview} className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={() => triggerScrape()} disabled={triggering !== null}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50">
              {triggering === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Scrape All Marketplaces
            </button>
          </div>
        </div>

        {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}

        {/* Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-2xl font-bold text-brand-600">{overview.available_scrapers.length}</p>
              <p className="text-xs text-gray-500">Marketplaces</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview.total_jobs}</p>
              <p className="text-xs text-gray-500">Total Jobs</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-2xl font-bold text-blue-600">{overview.running_jobs}</p>
              <p className="text-xs text-gray-500">Running Now</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-2xl font-bold text-green-600">{overview.last_completed?.domains_found || 0}</p>
              <p className="text-xs text-gray-500">Last Run Domains</p>
            </div>
          </div>
        )}

        {/* Individual Marketplace Triggers */}
        {overview && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Available Scrapers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {overview.available_scrapers.map(name => (
                <div key={name} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-brand-500" />
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{name}</span>
                  </div>
                  <button onClick={() => triggerScrape(name)} disabled={triggering !== null}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg flex items-center gap-1 disabled:opacity-50">
                    {triggering === name ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    Run
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Jobs */}
        {overview && overview.recent_jobs.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Recent Jobs</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marketplace</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Found</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Listings</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Errors</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {overview.recent_jobs.map(job => (
                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white capitalize">{job.marketplace}</td>
                      <td className="px-4 py-3">{statusBadge(job.status)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{job.domains_found}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{job.domains_created}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{job.listings_created}</td>
                      <td className="px-4 py-3">
                        {job.errors > 0 ? (
                          <span className="text-red-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{job.errors}</span>
                        ) : <span className="text-gray-400">0</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{job.created_at ? new Date(job.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
